/**
 * Facebook Pixel and Conversions API Integration for Life Itself Studio
 *
 * This module provides comprehensive Facebook tracking including:
 * - Standard e-commerce events (ViewContent, AddToCart, InitiateCheckout, Purchase, AddPaymentInfo)
 * - Custom plant-specific events
 * - Event deduplication via event_id for browser + server matching
 * - Automatic advanced matching with hashed email/phone
 * - Consent integration with the cookie consent system
 * - Dynamic product catalog integration for DPA
 *
 * NOTE: Facebook Pixel initialization is handled by @/lib/cookies.ts
 * This module focuses on tracking events and helper functions.
 */

// Facebook Pixel ID from environment
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID || '';

// Content IDs for product catalog integration
const CONTENT_TYPE_PRODUCT = 'product';
const CONTENT_TYPE_PRODUCT_GROUP = 'product_group';

// Currency used for all transactions
const CURRENCY = 'USD';

// Generate unique event ID for deduplication between browser and server events
export function generateEventId(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `btw_${timestamp}_${randomPart}`;
}

// Hash function for advanced matching (SHA-256)
async function sha256Hash(str: string): Promise<string> {
  const normalized = str.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Format phone number for Facebook (E.164 format without +)
function formatPhoneForFB(phone: string): string {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  // If US number without country code, add 1
  if (digits.length === 10) {
    return `1${digits}`;
  }
  return digits;
}

// =============================================================================
// Advanced Matching
// =============================================================================

export interface AdvancedMatchingData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  externalId?: string;
}

/**
 * Set advanced matching parameters for improved attribution
 * Uses hashed PII data for privacy-compliant matching
 */
export async function setAdvancedMatching(data: AdvancedMatchingData): Promise<void> {
  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return;

  const advancedMatchingData: Record<string, string> = {};

  try {
    if (data.email) {
      advancedMatchingData.em = await sha256Hash(data.email);
    }
    if (data.phone) {
      advancedMatchingData.ph = await sha256Hash(formatPhoneForFB(data.phone));
    }
    if (data.firstName) {
      advancedMatchingData.fn = await sha256Hash(data.firstName);
    }
    if (data.lastName) {
      advancedMatchingData.ln = await sha256Hash(data.lastName);
    }
    if (data.city) {
      advancedMatchingData.ct = await sha256Hash(data.city);
    }
    if (data.state) {
      // State should be 2-letter abbreviation
      advancedMatchingData.st = await sha256Hash(data.state.toLowerCase());
    }
    if (data.zipCode) {
      // Zip code - first 5 digits only
      advancedMatchingData.zp = await sha256Hash(data.zipCode.substring(0, 5));
    }
    if (data.country) {
      // Country code (lowercase 2-letter)
      advancedMatchingData.country = await sha256Hash(data.country.toLowerCase());
    }
    if (data.externalId) {
      advancedMatchingData.external_id = await sha256Hash(data.externalId);
    }

    // Re-initialize pixel with advanced matching data
    if (Object.keys(advancedMatchingData).length > 0) {
      window.fbq('init', FB_PIXEL_ID, advancedMatchingData);
      console.log('[FB Pixel] Advanced matching data set');
    }
  } catch (error) {
    console.error('[FB Pixel] Error setting advanced matching:', error);
  }
}

// =============================================================================
// Standard E-commerce Events
// =============================================================================

export interface FBProductItem {
  id: string;
  quantity: number;
  item_price?: number;
}

export interface ViewContentParams {
  contentIds: string[];
  contentName: string;
  contentCategory: string;
  value?: number;
  currency?: string;
  eventId?: string;
}

/**
 * Track ViewContent event when user views a product
 */
export function trackViewContent(params: ViewContentParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'ViewContent', {
    content_ids: params.contentIds,
    content_name: params.contentName,
    content_category: params.contentCategory,
    content_type: CONTENT_TYPE_PRODUCT,
    value: params.value || 0,
    currency: params.currency || CURRENCY,
  }, { eventID: eventId });

  console.log('[FB Pixel] ViewContent tracked:', params.contentName);
  return eventId;
}

export interface AddToCartParams {
  contentIds: string[];
  contentName: string;
  contentCategory: string;
  value: number;
  currency?: string;
  contents?: FBProductItem[];
  eventId?: string;
}

/**
 * Track AddToCart event
 */
export function trackAddToCart(params: AddToCartParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'AddToCart', {
    content_ids: params.contentIds,
    content_name: params.contentName,
    content_category: params.contentCategory,
    content_type: CONTENT_TYPE_PRODUCT,
    value: params.value,
    currency: params.currency || CURRENCY,
    contents: params.contents || params.contentIds.map(id => ({ id, quantity: 1 })),
  }, { eventID: eventId });

  console.log('[FB Pixel] AddToCart tracked:', params.contentName);
  return eventId;
}

export interface InitiateCheckoutParams {
  contentIds: string[];
  value: number;
  currency?: string;
  numItems: number;
  contents?: FBProductItem[];
  eventId?: string;
}

/**
 * Track InitiateCheckout event when user starts checkout process
 */
export function trackInitiateCheckout(params: InitiateCheckoutParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'InitiateCheckout', {
    content_ids: params.contentIds,
    content_type: CONTENT_TYPE_PRODUCT,
    value: params.value,
    currency: params.currency || CURRENCY,
    num_items: params.numItems,
    contents: params.contents || params.contentIds.map(id => ({ id, quantity: 1 })),
  }, { eventID: eventId });

  console.log('[FB Pixel] InitiateCheckout tracked');
  return eventId;
}

export interface AddPaymentInfoParams {
  contentIds: string[];
  value: number;
  currency?: string;
  contents?: FBProductItem[];
  eventId?: string;
}

/**
 * Track AddPaymentInfo event when user adds payment information
 */
export function trackAddPaymentInfo(params: AddPaymentInfoParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'AddPaymentInfo', {
    content_ids: params.contentIds,
    content_type: CONTENT_TYPE_PRODUCT,
    value: params.value,
    currency: params.currency || CURRENCY,
    contents: params.contents || params.contentIds.map(id => ({ id, quantity: 1 })),
  }, { eventID: eventId });

  console.log('[FB Pixel] AddPaymentInfo tracked');
  return eventId;
}

export interface PurchaseParams {
  contentIds: string[];
  value: number;
  currency?: string;
  numItems: number;
  contents?: FBProductItem[];
  orderId?: string;
  eventId?: string;
}

/**
 * Track Purchase event after successful checkout
 */
export function trackPurchase(params: PurchaseParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'Purchase', {
    content_ids: params.contentIds,
    content_type: CONTENT_TYPE_PRODUCT,
    value: params.value,
    currency: params.currency || CURRENCY,
    num_items: params.numItems,
    contents: params.contents || params.contentIds.map(id => ({ id, quantity: 1 })),
    order_id: params.orderId,
  }, { eventID: eventId });

  console.log('[FB Pixel] Purchase tracked:', params.orderId);
  return eventId;
}

// =============================================================================
// Custom Events for Plant-Specific Actions
// =============================================================================

export interface BiomeSelectionParams {
  biome: 'desert' | 'forest';
  eventId?: string;
}

/**
 * Track when user selects a biome
 */
export function trackBiomeSelection(params: BiomeSelectionParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('trackCustom', 'BiomeSelection', {
    biome_type: params.biome,
    content_category: 'Terrariums',
  }, { eventID: eventId });

  console.log('[FB Pixel] BiomeSelection tracked:', params.biome);
  return eventId;
}

export interface DeliveryWeekSelectionParams {
  deliveryWeek: string;
  eventId?: string;
}

/**
 * Track when user selects a delivery week
 */
export function trackDeliveryWeekSelection(params: DeliveryWeekSelectionParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('trackCustom', 'DeliveryWeekSelected', {
    delivery_week: params.deliveryWeek,
  }, { eventID: eventId });

  console.log('[FB Pixel] DeliveryWeekSelected tracked:', params.deliveryWeek);
  return eventId;
}

export interface InsuranceOptInParams {
  optedIn: boolean;
  monthlyValue: number;
  eventId?: string;
}

/**
 * Track when user opts in/out of plant insurance
 */
export function trackInsuranceOptIn(params: InsuranceOptInParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('trackCustom', 'InsuranceOptIn', {
    opted_in: params.optedIn,
    monthly_value: params.monthlyValue,
  }, { eventID: eventId });

  console.log('[FB Pixel] InsuranceOptIn tracked:', params.optedIn);
  return eventId;
}

export interface UpgradeSelectionParams {
  upgradeType: 'premium_plants' | 'custom_design' | 'experience';
  upgradeValue: number;
  experienceSize?: '2-person' | '4-person';
  eventId?: string;
}

/**
 * Track when user selects an upgrade
 */
export function trackUpgradeSelection(params: UpgradeSelectionParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('trackCustom', 'UpgradeSelected', {
    upgrade_type: params.upgradeType,
    upgrade_value: params.upgradeValue,
    experience_size: params.experienceSize,
  }, { eventID: eventId });

  console.log('[FB Pixel] UpgradeSelected tracked:', params.upgradeType);
  return eventId;
}

export interface GiftOrderParams {
  isGift: boolean;
  occasion?: string;
  eventId?: string;
}

/**
 * Track when user marks order as a gift
 */
export function trackGiftOrder(params: GiftOrderParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('trackCustom', 'GiftOrder', {
    is_gift: params.isGift,
    gift_occasion: params.occasion,
  }, { eventID: eventId });

  console.log('[FB Pixel] GiftOrder tracked:', params.isGift);
  return eventId;
}

export interface WaitlistSignupParams {
  state: string;
  biomePreference?: string;
  eventId?: string;
}

/**
 * Track waitlist signup for expansion tracking
 */
export function trackWaitlistSignup(params: WaitlistSignupParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'Lead', {
    content_name: 'Waitlist Signup',
    content_category: 'Waitlist',
    value: 10, // Estimated lead value
    currency: CURRENCY,
  }, { eventID: eventId });

  // Also track custom event with more details
  window.fbq('trackCustom', 'WaitlistSignup', {
    state: params.state,
    biome_preference: params.biomePreference,
  });

  console.log('[FB Pixel] WaitlistSignup tracked:', params.state);
  return eventId;
}

export interface NewsletterSignupParams {
  source: string;
  eventId?: string;
}

/**
 * Track newsletter signup
 */
export function trackNewsletterSignup(params: NewsletterSignupParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'Lead', {
    content_name: 'Newsletter Signup',
    content_category: 'Newsletter',
    value: 5, // Estimated lead value
    currency: CURRENCY,
  }, { eventID: eventId });

  console.log('[FB Pixel] NewsletterSignup tracked:', params.source);
  return eventId;
}

export interface WorkshopBookingParams {
  packageType: '2-person' | '4-person';
  workshopDate: string;
  value: number;
  eventId?: string;
}

/**
 * Track workshop booking
 */
export function trackWorkshopBooking(params: WorkshopBookingParams): string {
  const eventId = params.eventId || generateEventId();

  if (!FB_PIXEL_ID || typeof window.fbq !== 'function') return eventId;

  window.fbq('track', 'Schedule', {
    content_name: `Terrarium Workshop (${params.packageType})`,
    content_category: 'Workshops',
    value: params.value,
    currency: CURRENCY,
  }, { eventID: eventId });

  window.fbq('trackCustom', 'WorkshopBooked', {
    package_type: params.packageType,
    workshop_date: params.workshopDate,
    value: params.value,
  });

  console.log('[FB Pixel] WorkshopBooking tracked:', params.packageType);
  return eventId;
}

// =============================================================================
// Helper Functions for Life Itself Studio E-commerce
// =============================================================================

export interface CheckoutStateForFB {
  biome: string;
  hasPremiumPlants: boolean;
  hasCustomDesign: boolean;
  experienceType: 'none' | '2-person' | '4-person';
  wantsInsurance: boolean;
  isGift: boolean;
}

/**
 * Build content IDs array from checkout state for dynamic product ads
 */
export function buildContentIds(state: CheckoutStateForFB): string[] {
  const ids: string[] = [];

  // Base terrarium
  ids.push(`terrarium-${state.biome}`);

  // Upgrades
  if (state.hasPremiumPlants) {
    ids.push('upgrade-premium-plants');
  }
  if (state.hasCustomDesign) {
    ids.push('upgrade-custom-design');
  }
  if (state.experienceType !== 'none') {
    ids.push(`experience-${state.experienceType}`);
  }
  if (state.wantsInsurance) {
    ids.push('insurance-monthly');
  }

  return ids;
}

/**
 * Build contents array for Facebook tracking
 */
export function buildContents(state: CheckoutStateForFB): FBProductItem[] {
  const contents: FBProductItem[] = [];

  // Base terrarium pricing
  const basePrices: Record<string, number> = {
    desert: 499,
    forest: 499,
  };

  // Base terrarium
  contents.push({
    id: `terrarium-${state.biome}`,
    quantity: 1,
    item_price: basePrices[state.biome] || 499,
  });

  // Upgrades
  if (state.hasPremiumPlants) {
    contents.push({
      id: 'upgrade-premium-plants',
      quantity: 1,
      item_price: 499,
    });
  }
  if (state.hasCustomDesign) {
    contents.push({
      id: 'upgrade-custom-design',
      quantity: 1,
      item_price: 999,
    });
  }
  if (state.experienceType !== 'none') {
    const experiencePrices: Record<string, number> = {
      '2-person': 1501,
      '4-person': 3501,
    };
    contents.push({
      id: `experience-${state.experienceType}`,
      quantity: 1,
      item_price: experiencePrices[state.experienceType] || 0,
    });
  }
  if (state.wantsInsurance) {
    contents.push({
      id: 'insurance-monthly',
      quantity: 1,
      item_price: 20, // Monthly price
    });
  }

  return contents;
}

/**
 * Calculate total value from checkout state
 */
export function calculateTotalValue(state: CheckoutStateForFB): number {
  let total = 499; // Base terrarium

  if (state.hasPremiumPlants) total += 499;
  if (state.hasCustomDesign) total += 999;
  if (state.experienceType === '2-person') total += 1501;
  if (state.experienceType === '4-person') total += 3501;
  // Note: Insurance is recurring, not added to one-time total

  return total;
}

// =============================================================================
// Consent Management
// =============================================================================

/**
 * Check if marketing consent has been given
 */
export function hasMarketingConsent(): boolean {
  try {
    const stored = localStorage.getItem('btw_cookie_consent');
    if (!stored) return false;
    const preferences = JSON.parse(stored);
    return preferences.marketing === true;
  } catch {
    return false;
  }
}

/**
 * Revoke Facebook Pixel consent
 */
export function revokeConsent(): void {
  if (typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke');
    console.log('[FB Pixel] Consent revoked');
  }
}

/**
 * Grant Facebook Pixel consent
 */
export function grantConsent(): void {
  if (typeof window.fbq === 'function') {
    window.fbq('consent', 'grant');
    console.log('[FB Pixel] Consent granted');
  }
}

// =============================================================================
// Server-Side Event Forwarding
// =============================================================================

export interface ServerEventData {
  eventName: string;
  eventId: string;
  eventTime: number;
  eventSourceUrl: string;
  userData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    clientIpAddress?: string;
    clientUserAgent?: string;
    fbc?: string;
    fbp?: string;
    externalId?: string;
  };
  customData?: Record<string, unknown>;
}

/**
 * Send event to server for Conversions API forwarding
 * This enables server-side tracking for better accuracy with iOS privacy restrictions
 */
export async function sendServerEvent(data: ServerEventData): Promise<void> {
  try {
    // Get Facebook browser cookies for matching
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');

    const eventData: ServerEventData = {
      ...data,
      eventTime: data.eventTime || Math.floor(Date.now() / 1000),
      eventSourceUrl: data.eventSourceUrl || window.location.href,
      userData: {
        ...data.userData,
        fbc: fbc || undefined,
        fbp: fbp || undefined,
        clientUserAgent: navigator.userAgent,
      },
    };

    const response = await fetch('/api/analytics/fb-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      console.error('[FB CAPI] Failed to send server event:', response.statusText);
    } else {
      console.log('[FB CAPI] Server event sent:', data.eventName);
    }
  } catch (error) {
    console.error('[FB CAPI] Error sending server event:', error);
  }
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// =============================================================================
// Type Declarations
// =============================================================================

declare global {
  interface Window {
    fbq: FacebookPixelFunction;
    _fbq: FacebookPixelFunction;
  }
}

interface FacebookPixelFunction {
  (action: 'track', eventName: string, params?: Record<string, unknown>, options?: { eventID?: string }): void;
  (action: 'trackCustom', eventName: string, params?: Record<string, unknown>, options?: { eventID?: string }): void;
  (action: 'init', pixelId: string, advancedMatching?: Record<string, string>): void;
  (action: 'consent', type: 'revoke' | 'grant'): void;
  (...args: unknown[]): void;
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
  queue: unknown[][];
  callMethod?: (...args: unknown[]) => void;
}
